import { Component, effect, input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ChoiceQuestionTypes, Condition, MultiChoiceQuestionTypes, Operator } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { CopyChoicesConfirmationComponent } from './copy-choices-confirmation/copy-choices-confirmation.component';
import { CopyOptionsExclusionLevel, CopyOptionsExclusionLevelOptions } from '../../../models/survey-editor/choices-settings-form.constants';
import { IQuestionOptionForm } from '../../../models/type-forms/question-form-group.interface';
import { QuestionOptionForm } from '../../../models/type-forms/question-option-form.model';
import { SurveyEditorService } from '../../../services/survey-editor.service';

@Component({
  selector: 'empathy-choice-settings',
  templateUrl: 'choice-settings.component.html',
  styleUrls: ['./choice-settings.component.scss', './_choice-settings-theme.component.scss']
})
export class ChoiceSettingsComponent implements OnInit {
  activeQuestion = input<FormGroup>();

  questionToCopyExclusionLevel = new FormControl<CopyOptionsExclusionLevel>(CopyOptionsExclusionLevel.SelectedOnly);
  questionToCopyFromCtrl = new FormControl();
  questionToCopyOptionsAnswersFromCtrl = new FormControl();

  questionsToCopyOptionsAnswersFrom: Question[] = [];
  questionsToCopyOptionsFrom: Question[] = [];

  copyOptionsExclusionLevelOptions = CopyOptionsExclusionLevelOptions;
  choiceQuestionTypes = ChoiceQuestionTypes;

  constructor(
    private dialog: MatDialog,
    private surveyEditorService: SurveyEditorService
  ) {
    effect(() => {
      if (this.activeQuestion()) {
        this.questionsToCopyOptionsFrom = this.surveyEditorService
          .surveyEditor$()
          .allAllowedCopyOptionsTypeQuestions.filter((q) => q.id !== this.activeQuestion().value.id);

        this.questionsToCopyOptionsAnswersFrom = this.surveyEditorService
          .surveyEditor$()
          .previousChoiceSelectableQuestions.filter((q) => q.id !== this.activeQuestion().value.id);
      }
    });
  }

  get options(): FormArray<FormGroup<IQuestionOptionForm>> {
    return this.activeQuestion().get('options') as FormArray<FormGroup<IQuestionOptionForm>>;
  }

  ngOnInit(): void {
    this.questionToCopyOptionsAnswersFromCtrl.valueChanges.subscribe((questionId: string) => {
      this.questionToCopyOptionsAnswersFromCtrl.reset('', { emitEvent: false });

      if (this.activeQuestion().value.options.filter((o) => o.text || o.stimulus?.name || o.stimulus?.mediaId || o.stimulus?.type).length > 0) {
        const dialogRef = this.dialog.open(CopyChoicesConfirmationComponent);

        dialogRef.afterClosed().subscribe((action) => {
          if (!action) {
            return;
          }

          this.patchOptionsArrayWithAnswersConditionality(questionId);
        });
      } else {
        this.patchOptionsArrayWithAnswersConditionality(questionId);
      }
    });

    this.questionToCopyFromCtrl.valueChanges.subscribe((questionId: string) => {
      this.questionToCopyFromCtrl.reset('', { emitEvent: false });

      if (this.activeQuestion().value.options.filter((o) => o.text || o.stimulus?.name || o.stimulus?.mediaId || o.stimulus?.type).length > 0) {
        const dialogRef = this.dialog.open(CopyChoicesConfirmationComponent);

        dialogRef.afterClosed().subscribe((action) => {
          if (!action) {
            return;
          }

          this.patchOptionsArray(questionId);
        });
      } else {
        this.patchOptionsArray(questionId);
      }
    });
  }

  onSetOptionsExclusion(event: CopyOptionsExclusionLevel): void {
    this.questionToCopyExclusionLevel.setValue(event);
  }

  private patchOptionsArray(questionId: string): void {
    const question = this.questionsToCopyOptionsFrom.find((q) => q.id === questionId);

    if (!question) {
      return;
    }

    this.options.clear();

    const optionsToCopy = question.options || [];

    optionsToCopy.forEach((option) => {
      this.options.push(new QuestionOptionForm(option).form);
    });

    this.patchOtherOption(question);
  }

  private patchOptionsArrayWithAnswersConditionality(questionId: string): void {
    const question = this.questionsToCopyOptionsAnswersFrom.find((q) => q.id === questionId);
    if (!question) {
      return;
    }
    const exclusionLevel = this.questionToCopyExclusionLevel.value;
    const operator = this.getOperatorBasedOnExclusionLevel(exclusionLevel, question);
    this.options.clear();

    const optionsToCopy = this.questionsToCopyOptionsAnswersFrom.find((q) => q.id === questionId)?.options || [];

    optionsToCopy.forEach((option) => {
      const newOption = structuredClone(option);

      if (operator) {
        const condition: Condition = {
          questionId,
          operator: operator,
          cardId: null,
          valueToMatch: [option.id],
          group: null,
          optionId: null
        };

        newOption.visibleWhen = [condition];
      }

      this.options.push(new QuestionOptionForm(newOption).form);
    });

    this.patchOtherOption(question);
  }

  private patchOtherOption(question: Question): void {
    this.activeQuestion().get('useOtherInd').setValue(question.useOtherInd);
    this.activeQuestion().get('useOtherPosition').setValue(question.useOtherPosition);
    this.activeQuestion().get('otherLabel').setValue(question.otherLabel);
  }

  private getOperatorBasedOnExclusionLevel(exclusionLevel: CopyOptionsExclusionLevel, question: Question): Operator {
    switch (exclusionLevel) {
      case CopyOptionsExclusionLevel.SelectedOnly:
        return MultiChoiceQuestionTypes.includes(question.type) ? 'INCLUDES_ANY' : 'EQUAL';
      case CopyOptionsExclusionLevel.AllExceptSelected:
        return MultiChoiceQuestionTypes.includes(question.type) ? 'NOT_INCLUDES' : 'NOT_EQUAL';
      default:
        return null;
    }
  }
}
